import {
  type ConvidarCondutor,
  type RegimeDeFaturamento,
  type StatusDaConta,
  type TipoDeConta,
  regimeDeFaturamentoSchema,
  statusDaContaSchema,
  tipoDeContaSchema,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPatch, apiPost } from '@/lib/api'
import { ROTAS_DE_CONTAS } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

import {
  CHAVE_DE_USUARIOS,
  type PaginaDeUsuarios,
  paginaDeUsuariosSchema,
  usuarioSchema,
} from '../usuarios/api'

/**
 * Contas de cliente (PF e PJ) — o Gap #9 do ROADMAP.
 *
 * `Account` é QUEM PAGA; `User` é QUEM FAZ LOGIN. Numa PF os dois quase
 * coincidem; numa PJ nunca: a empresa é a titular, e sob ela existem N
 * condutores autorizados com acessos próprios. Por isso o vínculo de condutores
 * vive aqui, e não na tela de usuários internos.
 *
 * Dinheiro trafega como string de CENTAVOS (invariante nº 1) — `limiteDeCredito`
 * é `"150000"`, nunca `1500.0`.
 */

export const contaSchema = z.object({
  id: z.uuid(),
  tipo: tipoDeContaSchema,
  status: statusDaContaSchema,

  // Pessoa física
  cpf: z.string().nullable(),
  /**
   * ⚠️ `true` quando o `cpf` veio MASCARADO (`***.982.247-**`).
   *
   * A LISTAGEM mascara; o DETALHE não. Quem for preencher formulário com este
   * valor precisa do detalhe — salvar a máscara gravaria asteriscos no lugar
   * do documento.
   */
  documentoMascarado: z.boolean(),
  nome: z.string().nullable(),
  dataDeNascimento: z.string().nullable(),

  // Pessoa jurídica
  cnpj: z.string().nullable(),
  razaoSocial: z.string().nullable(),
  nomeFantasia: z.string().nullable(),
  inscricaoEstadual: z.string().nullable(),
  centroDeCusto: z.string().nullable(),

  // Comuns
  emailDeCobranca: z.string(),
  telefone: z.string().nullable(),
  regimeDeFaturamento: regimeDeFaturamentoSchema,
  diaDeFechamento: z.number().int().nullable(),
  /** Centavos como string decimal (o wire de `Money`). */
  limiteDeCredito: z.string().nullable(),
  tabelaDePrecoNegociadaId: z.string().nullable(),
  observacoes: z.string().nullable(),

  /** Quantos condutores autorizados a conta tem hoje. */
  quantidadeDeCondutores: z.number().int().nonnegative(),

  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Conta = z.infer<typeof contaSchema>

export const paginaDeContasSchema = z.object({
  itens: z.array(contaSchema),
  total: z.number().int().nonnegative(),
  pagina: z.number().int().positive(),
  tamanho: z.number().int().positive(),
})

export type PaginaDeContas = z.infer<typeof paginaDeContasSchema>

/**
 * Corpo de criação/edição de conta.
 *
 * É um mapa aberto, e isso é deliberado. O contrato real é a união
 * DISCRIMINADA `criarContaSchema` (PF ou PJ), e o formulário já roda o
 * `safeParse` do schema do tipo escolhido antes de chamar a mutação — a
 * validação de verdade acontece lá, com dígito verificador e tudo. Tipar aqui
 * como a união obrigaria uma conversão de tipo no formulário só para satisfazer
 * a inferência de `z.input` sobre campos com `coerce` e `default`, o que é
 * cerimônia sem ganho: o que protege este payload é o schema, não a assinatura.
 *
 * O que NUNCA pode aparecer neste objeto é `bigint` ou `Date` — eles são a
 * SAÍDA do schema, e `JSON.stringify` de um `bigint` estoura. Por isso o
 * formulário envia a ENTRADA validada (strings), nunca `validado.data`.
 */
export type CorpoDeConta = Readonly<Record<string, unknown>>

/** O nome de exibição depende do tipo: PF tem nome civil, PJ tem razão social. */
export function nomeDaConta(conta: Conta): string {
  const bruto = conta.tipo === 'PF' ? conta.nome : (conta.nomeFantasia ?? conta.razaoSocial)

  return bruto ?? 'sem nome'
}

/** O documento canônico da conta, sem máscara. */
export function documentoDaConta(conta: Conta): string | null {
  return conta.tipo === 'PF' ? conta.cpf : conta.cnpj
}

export interface FiltrosDeContas {
  readonly pagina: number
  readonly tamanho: number
  readonly busca: string
  readonly tipo: TipoDeConta | ''
  readonly status: StatusDaConta | ''
  readonly regimeDeFaturamento: RegimeDeFaturamento | ''
}

export const CHAVE_DE_CONTAS = 'contas'

export function useContas(filtros: FiltrosDeContas): UseQueryResult<PaginaDeContas> {
  return useQuery({
    queryKey: [CHAVE_DE_CONTAS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        busca: filtros.busca,
        tipo: filtros.tipo,
        status: filtros.status,
        regimeDeFaturamento: filtros.regimeDeFaturamento,
      })

      return apiGet(`${ROTAS_DE_CONTAS.lista}${query}`, paginaDeContasSchema, { signal })
    },
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Uma conta pelo id — com o documento EM CLARO.
 *
 * ⚠️ Existe porque a LISTAGEM mascara o CPF no servidor (Adendo E). A linha da
 * lista serve para exibir; para EDITAR é preciso o valor cheio, senão o
 * formulário salva `***.982.247-**` como documento.
 *
 * ⚠️ E buscar aqui não é gratuito: o detalhe grava `DADO_DE_CLIENTE_REVELADO`
 * na trilha. Por isso `enabled` — o hook só dispara quando há um id de fato,
 * e não a cada render da listagem.
 */
export function useConta(id: string | null): UseQueryResult<Conta> {
  return useQuery({
    queryKey: [CHAVE_DE_CONTAS, 'detalhe', id],
    queryFn: ({ signal }) => apiGet(ROTAS_DE_CONTAS.porId(id ?? ''), contaSchema, { signal }),
    enabled: id !== null,
    // Sem cache entre aberturas: cada edição é uma revelação, e servir do cache
    // esconderia da trilha a segunda vez que alguém abriu a mesma conta.
    staleTime: 0,
    gcTime: 0,
  })
}

export function useCriarConta(): UseMutationResult<Conta, Error, CorpoDeConta> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeConta) => apiPost(ROTAS_DE_CONTAS.lista, contaSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CONTAS] })
    },
  })
}

export interface AtualizacaoDeConta {
  readonly id: string
  /** Já validado pelo schema de atualização do tipo correspondente. */
  readonly dados: CorpoDeConta
}

export function useAtualizarConta(): UseMutationResult<Conta, Error, AtualizacaoDeConta> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeConta) =>
      apiPatch(ROTAS_DE_CONTAS.porId(id), contaSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CONTAS] })
    },
  })
}

/**
 * Condutores autorizados de uma conta.
 *
 * Reaproveita o modelo de leitura de usuário: condutor É um usuário, com papel
 * `CLIENTE` e preso ao `accountId` da conta. Ter dois modelos para a mesma
 * tabela seria convidar os dois a divergirem.
 */
export function useCondutores(contaId: string | null): UseQueryResult<PaginaDeUsuarios> {
  return useQuery({
    queryKey: [CHAVE_DE_CONTAS, contaId, 'condutores'],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_CONTAS.condutores(contaId ?? ''), paginaDeUsuariosSchema, { signal }),
    // Sem conta selecionada não existe pergunta a fazer.
    enabled: contaId !== null,
  })
}

export interface ConviteDeCondutor {
  readonly contaId: string
  readonly dados: ConvidarCondutor
}

export function useConvidarCondutor(): UseMutationResult<
  z.infer<typeof usuarioSchema>,
  Error,
  ConviteDeCondutor
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ contaId, dados }: ConviteDeCondutor) =>
      apiPost(ROTAS_DE_CONTAS.condutores(contaId), usuarioSchema, dados),
    onSuccess: (_resultado, variaveis) => {
      void queryClient.invalidateQueries({
        queryKey: [CHAVE_DE_CONTAS, variaveis.contaId, 'condutores'],
      })
      // A contagem de condutores da lista de contas também mudou.
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CONTAS] })
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_USUARIOS] })
    },
  })
}
